/**
 * BP-026 · User Administration + AI Prompt Library — companion admin pages.
 * Kept lean; sit alongside PlatformMisc.jsx.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, MessageSquareCode, Plus, Power, PowerOff } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";

/* ─── User Administration ─────────────────────────────── */

export function PlatformUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      // Reuse the tenant list — every tenant row exposes user_count + last_login_at.
      const t = await apiGet(
        `/api/v3/platform/workspaces?limit=100${q ? "&q=" + encodeURIComponent(q) : ""}`
      );
      // Also pull role assignments to overlay platform roles.
      const roles = await apiGet("/api/v3/platform/roles");
      const roleMap = {};
      (roles.assignments || []).forEach((r) => {
        (roleMap[r.user_id] ||= []).push(r.role);
      });
      setRows(
        (t || []).map((w) => ({
          ...w,
          platform_roles: roleMap[w.id] || [],
        }))
      );
    } catch (e) {
      toast.error(e?.message || "Load failed");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const impersonate = async (userId, email) => {
    const reason = prompt(`Impersonate ${email || userId} — reason?`) || "";
    if (!reason) return;
    try {
      const res = await apiPost("/api/v3/platform/impersonate", {
        target_user_id: userId,
        reason,
      });
      toast.success(
        `Impersonation token minted (60min max). Copy: ${res.access_token.slice(0, 20)}…`
      );
      navigator.clipboard?.writeText(res.access_token);
    } catch (e) {
      toast.error(e?.message || "Impersonate failed");
    }
  };

  return (
    <div data-testid="platform-users" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <Users className="w-4 h-4" /> User administration
        </div>
        <h1 className="text-2xl font-bold">Users across tenants</h1>
      </header>
      <div className="flex gap-2">
        <input
          data-testid="users-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search email / id / name…"
          className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm w-96"
        />
        <button
          onClick={load}
          disabled={busy}
          className="rounded-md bg-slate-100 border border-slate-300 px-3 py-1.5 text-sm text-slate-900 hover:bg-slate-700"
        >
          Search
        </button>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Account</th>
              <th className="px-4 py-2">Users</th>
              <th className="px-4 py-2">Last login</th>
              <th className="px-4 py-2">Platform roles</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((t) => (
              <tr key={t.id} data-testid={`user-row-${t.id}`}>
                <td className="px-4 py-3">
                  <div className="text-slate-900 truncate max-w-[280px]">
                    {t.name}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 truncate max-w-[300px]">
                    {t.id}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">{t.user_count}</td>
                <td className="px-4 py-3 text-slate-500 text-xs">
                  {t.last_login_at?.slice(0, 19) || "—"}
                </td>
                <td className="px-4 py-3">
                  {t.platform_roles.length ? (
                    <div className="flex flex-wrap gap-1">
                      {t.platform_roles.map((r) => (
                        <span
                          key={r}
                          className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-400 px-2 py-0.5 text-[10px]"
                        >
                          {r}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`user-impersonate-${t.id}`}
                    onClick={() => impersonate(t.id, t.name)}
                    className="rounded-md border border-purple-700 text-purple-300 hover:bg-purple-900/40 px-2 py-1 text-xs"
                  >
                    Impersonate
                  </button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─── AI Prompt Library ───────────────────────────────── */

export function PlatformPrompts() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({
    bp_tag: "BP-025",
    name: "coach_narrate_analysis",
    prompt_version: "v1",
    system_prompt: "",
    activate: true,
  });

  const load = async () => {
    setRows(await apiGet("/api/v3/platform/prompts"));
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.system_prompt) return toast.error("system_prompt required");
    try {
      await apiPost("/api/v3/platform/prompts", form);
      toast.success("Prompt saved");
      setForm((f) => ({ ...f, system_prompt: "" }));
      load();
    } catch (e) {
      toast.error(e?.message || "Save failed");
    }
  };
  const deactivate = async (id) => {
    await apiPost(`/api/v3/platform/prompts/${id}/deactivate`, {});
    load();
  };

  return (
    <div data-testid="platform-prompts" className="p-8 space-y-6">
      <header>
        <div className="text-xs uppercase text-slate-500 flex items-center gap-1">
          <MessageSquareCode className="w-4 h-4" /> Prompt library
        </div>
        <h1 className="text-2xl font-bold">AI system prompts</h1>
        <p className="text-xs text-slate-600 mt-1">
          Registered prompts per BP + name. Activating a new version auto-deactivates the previous.
        </p>
      </header>
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input
            data-testid="prompt-bp"
            value={form.bp_tag}
            onChange={(e) => setForm((f) => ({ ...f, bp_tag: e.target.value }))}
            placeholder="bp_tag"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <input
            data-testid="prompt-name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="name"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <input
            data-testid="prompt-version"
            value={form.prompt_version}
            onChange={(e) =>
              setForm((f) => ({ ...f, prompt_version: e.target.value }))
            }
            placeholder="prompt_version"
            className="bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm"
          />
          <button
            data-testid="prompt-save"
            onClick={save}
            className="rounded-md bg-emerald-600 hover:bg-emerald-500 text-slate-900 text-sm flex items-center justify-center gap-1"
          >
            <Plus className="w-4 h-4" /> Save
          </button>
        </div>
        <textarea
          data-testid="prompt-system"
          value={form.system_prompt}
          onChange={(e) =>
            setForm((f) => ({ ...f, system_prompt: e.target.value }))
          }
          rows={5}
          placeholder="System prompt body (markdown ok)"
          className="mt-2 w-full bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm font-mono"
        />
      </section>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">BP</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Version</th>
              <th className="px-4 py-2">Active</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((p) => (
              <tr key={p.id} data-testid={`prompt-row-${p.id}`}>
                <td className="px-4 py-2 text-slate-900">{p.bp_tag}</td>
                <td className="px-4 py-2 text-slate-700 font-mono text-xs">
                  {p.name}
                </td>
                <td className="px-4 py-2 text-slate-600">{p.prompt_version}</td>
                <td className="px-4 py-2">
                  {p.active ? (
                    <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[11px] flex items-center gap-1 w-fit">
                      <Power className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-500/20 text-slate-600 px-2 py-0.5 text-[11px] flex items-center gap-1 w-fit">
                      <PowerOff className="w-3 h-3" /> Retired
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-500 text-xs">
                  {p.created_at?.slice(0, 19)}
                </td>
                <td className="px-4 py-2 text-right">
                  {p.active && (
                    <button
                      data-testid={`prompt-deactivate-${p.id}`}
                      onClick={() => deactivate(p.id)}
                      className="rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-2 py-1 text-xs"
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  No prompts registered yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
